/**
 * Inline editable primitives for the segment detail header.
 *
 * Each primitive renders as plain text by default. Click commits to edit mode;
 * Enter (or Cmd+Enter for textarea) saves; Escape cancels. Editing chrome only
 * appears once engaged, keeping the resting state editorial.
 */

import { useEffect, useRef, useState } from 'react'
import { Sparkles } from 'lucide-react'
import { SECTORS, type Sector } from '@ispot/shared'

const SECTOR_LABEL: Record<Sector, string> = {
  private: 'Private',
  public: 'Public',
  cross: 'Cross-sector',
}

interface EditableTextProps {
  value: string
  ariaLabel: string
  placeholder: string
  className?: string
  inputClassName?: string
  onCommit: (value: string) => void | Promise<void>
  required?: boolean
}

export function EditableText({
  value,
  ariaLabel,
  placeholder,
  className = '',
  inputClassName = '',
  onCommit,
  required = false,
}: EditableTextProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  const commit = async () => {
    setEditing(false)
    const next = draft
    if (required && !next.trim()) return
    if (next === value) return
    await onCommit(next)
  }
  const cancel = () => {
    setDraft(value)
    setEditing(false)
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        autoFocus
        type="text"
        value={draft}
        aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void commit()
          if (e.key === 'Escape') cancel()
        }}
        className={`min-w-0 flex-1 rounded-md border bg-white px-2 py-1 outline-none focus:ring-2 ${inputClassName}`}
        style={{
          borderColor: 'var(--color-border)',
          color: 'var(--color-text)',
          ['--tw-ring-color' as string]: 'var(--color-accent, #e84300)',
        }}
      />
    )
  }

  const isEmpty = value.trim().length === 0
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      aria-label={`Edit ${ariaLabel}`}
      className={`-mx-1 inline-flex max-w-full cursor-text items-baseline rounded px-1 text-left transition-colors hover:bg-[var(--color-warm-100)] ${className}`}
      style={{
        color: isEmpty ? 'var(--color-text-muted)' : 'var(--color-text)',
        fontStyle: isEmpty ? 'italic' : undefined,
        fontWeight: isEmpty ? 400 : undefined,
      }}
    >
      <span className="truncate">{isEmpty ? placeholder : value}</span>
    </button>
  )
}

export function SectorPicker({
  value,
  onChange,
}: {
  value: Sector
  onChange: (s: Sector) => void
}) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full p-1"
      style={{ backgroundColor: 'var(--color-warm-100)' }}
    >
      {SECTORS.map((s) => {
        const active = s === value
        return (
          <button
            key={s}
            type="button"
            onClick={() => active || onChange(s)}
            className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors"
            style={{
              backgroundColor: active ? 'var(--color-bg, white)' : 'transparent',
              color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
              boxShadow: active ? '0 1px 2px rgba(29,26,21,0.06)' : undefined,
            }}
            aria-pressed={active}
          >
            {SECTOR_LABEL[s]}
          </button>
        )
      })}
    </span>
  )
}

export function EditableDescription({
  value,
  onCommit,
  onSuggest,
}: {
  value: string
  onCommit: (value: string) => void | Promise<void>
  onSuggest?: () => Promise<string | null>
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [suggesting, setSuggesting] = useState(false)
  const taRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  useEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus()
      taRef.current.setSelectionRange(taRef.current.value.length, taRef.current.value.length)
    }
  }, [editing])

  const commit = async () => {
    setEditing(false)
    if (draft === value) return
    await onCommit(draft)
  }
  const cancel = () => {
    setDraft(value)
    setEditing(false)
  }
  const suggest = async () => {
    if (!onSuggest || suggesting) return
    setSuggesting(true)
    try {
      const next = await onSuggest()
      if (typeof next === 'string' && next.trim().length > 0) {
        setDraft(next)
        setEditing(true)
      }
    } catch {
      /* swallow */
    } finally {
      setSuggesting(false)
    }
  }

  const isEmpty = value.trim().length === 0

  if (editing) {
    return (
      <div className="mt-3 max-w-3xl">
        <textarea
          ref={taRef}
          value={draft}
          aria-label="Description"
          rows={Math.min(8, Math.max(3, draft.split('\n').length + 1))}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel()
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void commit()
          }}
          className="w-full rounded-md border bg-white px-3 py-2 text-[15px] leading-6 outline-none focus:ring-2"
          style={{
            borderColor: 'var(--color-border)',
            color: 'var(--color-text)',
            ['--tw-ring-color' as string]: 'var(--color-accent, #e84300)',
          }}
        />
        <div
          className="mt-2 flex items-center justify-between text-[11px]"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <span>
            <kbd className="rounded bg-[var(--color-warm-100)] px-1.5 py-0.5 font-mono text-[10px]">⌘ Enter</kbd>{' '}
            save ·{' '}
            <kbd className="rounded bg-[var(--color-warm-100)] px-1.5 py-0.5 font-mono text-[10px]">Esc</kbd>{' '}
            cancel
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={cancel}
              className="rounded-md px-2 py-1 font-medium hover:bg-[var(--color-warm-100)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void commit()}
              className="rounded-md bg-[var(--color-text)] px-2.5 py-1 font-semibold text-white"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="group relative mt-3 max-w-3xl">
      <button
        type="button"
        onClick={() => setEditing(true)}
        aria-label="Edit description"
        className="-mx-2 block w-full cursor-text rounded-md px-2 py-1 text-left text-[15px] leading-6 transition-colors hover:bg-[var(--color-warm-100)]"
        style={{
          color: isEmpty ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
          fontStyle: isEmpty ? 'italic' : undefined,
        }}
      >
        {isEmpty ? 'No description yet — click to add one, or use Skerpa to draft it.' : value}
      </button>
      {onSuggest ? (
        <button
          type="button"
          onClick={suggest}
          disabled={suggesting}
          className="absolute right-0 top-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 disabled:opacity-100"
          style={{
            backgroundColor: 'var(--color-market-50, #e6f8f9)',
            color: 'var(--color-market-700, #008087)',
          }}
          title="Draft a description from the segment brief"
        >
          <Sparkles className={`h-3 w-3 ${suggesting ? 'animate-pulse' : ''}`} />
          {suggesting ? 'Drafting…' : 'Suggest'}
        </button>
      ) : null}
    </div>
  )
}
